import { useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { useTransition } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { gql } from '@/lib/graphql/gql'
import type {
  DeleteRegistryCredentialMutation,
  DeleteRegistryCredentialMutationVariables,
} from '@/routes/registry-credentials/delete-registry-credential-dialog/index.types.gen'

const DELETE_REGISTRY_CREDENTIAL = gql`
  mutation DeleteRegistryCredential($idRegistryCredential: String!) {
    deleteDockerRegistryCredential(id: $idRegistryCredential)
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const DeleteRegistryCredentialDialog = ({ isOpen, onClose }: Props) => {
  const { idRegistryCredential = '' } = useParams<{
    idRegistryCredential: string
  }>()
  const [isPending, startTransition] = useTransition()

  const [deleteRegistryCredential] = useMutation<
    DeleteRegistryCredentialMutation,
    DeleteRegistryCredentialMutationVariables
  >(DELETE_REGISTRY_CREDENTIAL)

  return (
    <AlertDialog open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete registry credential</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the registry credential?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() => {
              startTransition(async () => {
                try {
                  await deleteRegistryCredential({
                    variables: { idRegistryCredential },
                  })
                  toast(
                    <ToasterContent variant="success">
                      Registry credential deleted successfully
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                } catch {
                  toast(
                    <ToasterContent variant="error">
                      Failed to delete registry credential
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                }
              })
            }}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
